import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, X, LogOut, Shield } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { user, logout } = useAuth();
  const { content } = useContent();
  const menu = content.nav_menu || [];
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/loja?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setMobileOpen(false);
    }
  };

  return (
    <header className="glass-nav sticky top-0 z-40" data-testid="navbar">
      {content.announcement?.active && content.announcement?.text && (
        <div className="brand-primary text-center text-xs py-2 px-4" data-testid="announcement-bar">
          {content.announcement.link ? (
            <a href={content.announcement.link} className="hover:underline">{content.announcement.text}</a>
          ) : (
            <span>{content.announcement.text}</span>
          )}
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <button
            className="lg:hidden brand-text"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>

          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <span className="font-serif text-2xl tracking-tight brand-text">{content.header?.logo_text || "Au and Co."}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {menu.map((m, i) => (
              <NavLink key={i} to={m.path} end={m.path === "/"} className="nav-link" data-testid={`nav-item-${i}`}>{m.label}</NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <button onClick={() => setSearchOpen((v) => !v)} className="brand-text hover:brand-primary-text transition" data-testid="search-toggle" aria-label="Procurar">
              <Search size={20} />
            </button>

            {user ? (
              <div className="relative group">
                <button className="brand-text hover:brand-primary-text transition flex items-center gap-2" data-testid="user-menu">
                  <User size={20} />
                  <span className="hidden md:inline text-sm">{user.name?.split(" ")[0]}</span>
                </button>
                <div className="absolute right-0 mt-2 w-56 brand-bg brand-border border shadow-[0_4px_24px_rgba(0,0,0,0.06)] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link to="/conta" className="block px-4 py-3 text-sm hover:brand-surface" data-testid="link-account">A minha conta</Link>
                  {user.role === "admin" && (
                    <Link to="/admin" className="block px-4 py-3 text-sm hover:brand-surface flex items-center gap-2" data-testid="link-admin">
                      <Shield size={14} /> Painel Admin
                    </Link>
                  )}
                  <button onClick={logout} className="w-full text-left px-4 py-3 text-sm hover:brand-surface flex items-center gap-2" data-testid="btn-logout">
                    <LogOut size={14} /> Terminar sessão
                  </button>
                </div>
              </div>
            ) : (
              <Link to="/entrar" className="brand-text hover:brand-primary-text transition" data-testid="link-login">
                <User size={20} />
              </Link>
            )}

            <button onClick={() => setOpen(true)} className="relative brand-text hover:brand-primary-text transition" data-testid="btn-cart">
              <ShoppingBag size={20} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 brand-primary text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={onSearch} className="pb-4" data-testid="search-form">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Procurar produtos..."
              className="input-line"
              data-testid="search-input"
            />
          </form>
        )}

        {mobileOpen && (
          <nav className="lg:hidden pb-6 space-y-4" data-testid="mobile-menu">
            {menu.map((m, i) => (
              <NavLink key={i} to={m.path} end={m.path === "/"} className="block nav-link" onClick={() => setMobileOpen(false)}>{m.label}</NavLink>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}
